import { useRef, useState } from "react";
import { Box, Button, IconButton, MenuItem, Dialog } from "@mui/material";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import {
  Visibility as ViewIcon,
  PersonRemove as DissociateIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import MaterialReactTable from "material-react-table";
import { useAuthUser } from "react-auth-kit";
import {
  ViewDevice,
  useGetPatientDevices,
  useDissociateFromPatient,
} from "../../DeviceComponents";
import ConfirmDialog from "../../ConfirmDialog/ConfirmDialog";
import {
  LoadingPaper,
  EmptyPaper,
  LoadingError,
} from "../../LoadingComponents";
import columns from "./DispositiviColumns";
import AssociateDeviceDialog from "./AssociateDeviceDialog";

export default function DispositiviPatient({ patient_id }) {
  const authUser = useAuthUser();
  const canManageDevice = authUser()?.role === 1;
  const tableColumns = columns;

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [openViewDevice, setOpenViewDevice] = useState(false);
  const [deviceToDissociate, setDeviceToDissociate] = useState(null);
  const [openAssociateDevice, setOpenAssociateDevice] = useState(false);

  const handleOpenViewDevice = (deviceId) => {
    setSelectedDevice(deviceId);
    setOpenViewDevice(true);
  };
  const handleCloseViewDevice = () => {
    setOpenViewDevice(false);
  };
  const openInEditMode = useRef(false);

  const patientDevicesQuery = useGetPatientDevices(patient_id);
  const dissociateMutation = useDissociateFromPatient();

  const handleConfirmDissociate = () => {
    if (deviceToDissociate) {
      dissociateMutation.mutate(deviceToDissociate);
    }
    setDeviceToDissociate(null);
  };

  const devices = patientDevicesQuery.data?.devices ?? [];

  return (
    <Box
      sx={{
        display: "block",
        margin: "auto",
        width: "90%",
      }}
    >
      <AssociateDeviceDialog
        open={openAssociateDevice}
        patient_id={patient_id}
        onClose={() => setOpenAssociateDevice(false)}
      />

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DeviceHubIcon />}
          onClick={() => setOpenAssociateDevice(true)}
        >
          Associa/disconnetti dispositivo
        </Button>
      </Box>

      <ConfirmDialog
        open={deviceToDissociate !== null}
        title="Dissocia dispositivo"
        message={`Vuoi dissociare il dispositivo "${deviceToDissociate?.name ?? ""}" da questo paziente?`}
        confirmLabel="Dissocia"
        onCancel={() => setDeviceToDissociate(null)}
        onConfirm={handleConfirmDissociate}
      />

      <Dialog open={openViewDevice} fullScreen>
        <ViewDevice
          device_id={selectedDevice}
          closeModal={handleCloseViewDevice}
          _editMode={openInEditMode.current}
        />
      </Dialog>

      {patientDevicesQuery.isLoading ? (
        <LoadingPaper />
      ) : patientDevicesQuery.isError ? (
        <LoadingError error={patientDevicesQuery.error} />
      ) : devices.length === 0 ? (
        <EmptyPaper message="Nessun dispositivo associato al paziente" />
      ) : (
        <MaterialReactTable
          columns={tableColumns}
          data={devices}
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination={false}
          enableSorting={false}
          enableBottomToolbar={false}
          enableTopToolbar={false}
          muiTableBodyRowProps={{ hover: false }}
          muiTablePaperProps={{ elevation: 0 }}
          enableRowActions={true}
          displayColumnDefOptions={{
            "mrt-row-actions": { header: "Azioni" },
          }}
          positionActionsColumn="last"
          renderRowActionMenuItems={({ closeMenu, row }) => {
            const items = [
              <MenuItem
                key="view"
                onClick={() => {
                  openInEditMode.current = false;
                  handleOpenViewDevice(row.original.id);
                  closeMenu();
                }}
              >
                <IconButton>
                  <ViewIcon color="primary" />
                </IconButton>
                Visualizza
              </MenuItem>,
            ];

            if (canManageDevice) {
              items.push(
                <MenuItem
                  key="edit"
                  onClick={() => {
                    openInEditMode.current = true;
                    handleOpenViewDevice(row.original.id);
                    closeMenu();
                  }}
                >
                  <IconButton>
                    <EditIcon color="warning" />
                  </IconButton>
                  Modifica
                </MenuItem>
              );
            }

            items.push(
              <MenuItem
                key="dissociate"
                onClick={() => {
                  setDeviceToDissociate(row.original);
                  closeMenu();
                }}
              >
                <IconButton>
                  <DissociateIcon color="error" />
                </IconButton>
                Dissocia
              </MenuItem>
            );

            return items;
          }}
        />
      )}
    </Box>
  );
}
